import { Position } from '@xyflow/react';
import Decimal from 'decimal.js';
import { compact, groupBy, isNil, keyBy, uniq } from 'lodash';
import { ReactNode } from 'react';

import { getOutOfEstateCategoryIcon } from '@/components/diagrams/components/Tile/Tile.utils';
import { TileVariant } from '@/components/diagrams/components/Tile/types';
import { Edge, TileNode } from '@/components/diagrams/FlowChart';
import { TileNodeBeforeAndAfter } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNodeBeforeAndAfter';
import { Grid01Icon } from '@/components/icons/Grid01Icon';
import { getDisplayForWaterfallGrantorDeath } from '@/hooks/useWaterfallGrantorDeathDisplay';
import { GraphVizNodeComponentType } from '@/modules/graphViz/graphVizNodeConfig';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { AfterDeath, EntityStage } from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { diagnostics } from '@/utils/diagnostics';
import { formatCurrency } from '@/utils/formatting/currency';
import { FlowChartGraph } from '@/utils/graphology/FlowChartGraph';
import { getNodes } from '@/utils/graphqlUtils';

import { EstateWaterfallTileEditModal } from '../components/EstateWaterfallTileEditModal';
import {
  getCombinedTaxStateAndFederalAndGst,
  getIsPouroverEdge,
  hasDisplayableTax,
  isHypotheticalWaterfall,
} from '../EstateWaterfall.utils';
import {
  EstateWaterfall_GiftTaxSummaryFragment,
  EstateWaterfall_NodeFragment,
  EstateWaterfall_TaxSummaryV2Fragment,
  EstateWaterfallFragment,
} from '../graphql/EstateWaterfall.generated';
import {
  EstateWaterfallGraph,
  EstateWaterfallGraphAttributes,
  EstateWaterfallGraphEdgeAttributes,
  EstateWaterfallGraphNodeAttributes,
  GraphNodeCategorizationType,
  GroupNodeMember,
} from '../types';
import {
  ESTATE_TAX_NODE_IDENTIFIER,
  EXTERNAL_TRANSFER_NODE_IDENTIFIER,
  SECTION_PLACEHOLDER_TILE_NODE_IDENTIFIER,
} from './constants';
import { SHOW_CREATE_GROUP_MODAL_SENTINEL } from './CreateNewGroupModal.utils';
import { drawEdges } from './drawEdges';
import { GroupNodeItems } from './GroupNodeItems';
import { ManageGroupModal } from './ManageGroupModal';
import { TaxBreakout } from './TaxBreakout';
import { TaxLineOne } from './TaxLineOne';
import { TaxLineThree } from './TaxLineThree';
import {
  addTaxEdge,
  buildTileFromNode,
  getBeforeAndAfterData,
  getCategorizationType,
  getEstateTaxAfterDeath,
  getExternalTransferNodeId,
  getFeatureFlaggedEdgeLabel,
  getGiftTaxNodeId,
  getGroupNodeTileVariant,
  getNodeId,
  getTaxNodeId,
  getWaterfallNodeDisplayName,
  isEntityNode,
  isTaxNode,
} from './utils';

export const createEdgeId = (source: string, target: string) =>
  `${source}:${target}`;

// TODO (LUM-2026): Very duplicative with entity map, share this
export type EdgeInput = Omit<Edge, 'id' | 'type'>;
export const createEdge = ({
  source,
  target,
  data,
  ...edge
}: EdgeInput): Edge => ({
  id: createEdgeId(source, target),
  source,
  target,
  type: 'arrow',
  data: { hideLabel: true, ...data },
  sourceHandle: Position.Bottom,
  targetHandle: Position.Top,
  ...edge,
});

interface CreateWaterfallGraphInput {
  waterfall: EstateWaterfallFragment;
  grantors: ContextPrimaryClient[];
  isTwoClientHousehold: boolean;
  visibleNodeIds: string[];
  isGeneratingDefaultState?: boolean;
  initWaterfallNodeIds: Set<string>;
}

export function createWaterfallGraph({
  grantors,
  waterfall,
  isTwoClientHousehold,
  visibleNodeIds,
  isGeneratingDefaultState = false,
  initWaterfallNodeIds,
}: CreateWaterfallGraphInput): EstateWaterfallGraph {
  const graph = new FlowChartGraph<
    EstateWaterfallGraphNodeAttributes,
    EstateWaterfallGraphEdgeAttributes,
    EstateWaterfallGraphAttributes
  >();

  const isHypothetical = isHypotheticalWaterfall(waterfall);

  const {
    visualizationWithProjections,
    visualizationConfig,
    firstGrantorDeath,
    firstGrantorDeathYear,
    secondGrantorDeathYear,
    visualizationGroups,
    outgoingExternalTransfers,
    incomingExternalTransfers,
    hypotheticalTransfers,
  } = waterfall;

  const baseNodes: EstateWaterfall_NodeFragment[] | undefined = isHypothetical
    ? (waterfall?.nonHypotheticalVisualizationWithProjections?.nodes ??
      undefined)
    : undefined;

  const viz = visualizationWithProjections;

  // Filter out entities in PRE_CREATED stage - they should not appear in the waterfall
  const vizNodes = viz.nodes.filter((node) => {
    if (node.node.__typename === 'Entity') {
      return node.node.stage !== EntityStage.PreCreated;
    }
    return true;
  });

  const {
    beforeFirstDeathTaxSummaryV2,
    firstDeathTaxSummaryV2,
    secondDeathTaxSummaryV2,
  } = viz;
  const groupedNodes = vizNodes.filter((n) => !!n.group?.id);

  function nodeIdHasConfiguration(nodeId: string) {
    return waterfall.nodeConfigurations.some(
      (config) => config.nodeID === nodeId && !isNil(config.xPosition)
    );
  }

  const noneOfTheVizNodesHaveConfigurations = (() => {
    const numVizNodesWithConfig = vizNodes.filter((vizNode) =>
      nodeIdHasConfiguration(
        getNodeId({
          id: vizNode.group?.id ?? vizNode.id,
          afterDeath: vizNode.afterDeath,
        })
      )
    ).length;

    return numVizNodesWithConfig === 0;
  })();

  function getIsNewNodeById(id: string) {
    if (noneOfTheVizNodesHaveConfigurations) {
      // If we don't have any node configurations, only nodes not included in the default state are new
      if (isGeneratingDefaultState) {
        // If we're generating the initial graph with no configurations, so none of the nodes should be considered new
        return false;
      }

      if (!initWaterfallNodeIds.has(id) && !isTaxNode(id)) {
        // This node was not included in the initial state and should be considered new
        return true;
      }

      // All other nodes are not considered new
      return false;
    }
    return !nodeIdHasConfiguration(id);
  }

  // { None:groupviz_uuid1: Node[], First:groupviz_uuid1: Node[] }
  const groupedNodesByGroupNodeId = groupBy(groupedNodes, (n) =>
    // Nodes were pre-filtered to guarantee they include a group id
    getNodeId({ id: n.group!.id, afterDeath: n.afterDeath })
  ) as Record<string, EstateWaterfall_NodeFragment[]>;

  // { None:groupviz_uuid1: GraphVizGroup, First:groupviz_uuid1: GraphVizGroup }
  const visualizationGroupsByGroupNodeId = keyBy(
    getNodes(visualizationGroups),
    (g) => getNodeId({ id: g.id, afterDeath: g.namespace as AfterDeath })
  );

  // Always show at least the first two sections
  const deathSections = [AfterDeath.None, AfterDeath.First];
  if (isTwoClientHousehold) {
    deathSections.push(AfterDeath.Second);
  }
  const parentNodeIds = new Set(deathSections);

  // Build the section group nodes
  parentNodeIds.forEach((parentNodeId) => {
    const afterDeath = parentNodeId;

    const label = getDisplayForWaterfallGrantorDeath({
      afterDeath,
      primaryClients: grantors,
      isTwoClientHousehold,
      firstGrantorDeathYear,
      secondGrantorDeathYear,
      firstDyingGrantorId: firstGrantorDeath.id,
    });

    graph.addNodeSafe(parentNodeId, {
      data: { afterDeath, id: parentNodeId },
      node: {
        id: parentNodeId,
        position: { x: 0, y: 0 },
        data: { label },
        type: 'sectionLabel',
        draggable: false,
        selectable: false,
      },
      categorizationType: GraphNodeCategorizationType.SectionLabel,
    });
  });

  // Build the external transfer nodes
  const outgoingTransferSourceNodes = compact(
    getNodes(outgoingExternalTransfers).map((transfer) => {
      const sourceId =
        transfer.sourceEntity?.id || transfer.sourceIndividual?.id;
      if (!sourceId) return null;

      if (
        !visibleNodeIds.includes(
          getNodeId({ id: sourceId, afterDeath: AfterDeath.None })
        )
      ) {
        return null;
      }

      const transferAmount = transfer.transferValue || new Decimal(0);

      if (transferAmount.lessThanOrEqualTo(0)) {
        return null;
      }

      return {
        sourceId,
        transferAmount,
      };
    })
  );

  const outgoingTransferNodeId = getExternalTransferNodeId('outgoing');
  if (outgoingTransferSourceNodes.length) {
    graph.addNodeSafe(outgoingTransferNodeId, {
      data: {
        afterDeath: AfterDeath.None,
        id: EXTERNAL_TRANSFER_NODE_IDENTIFIER,
      },
      node: {
        id: outgoingTransferNodeId,
        type: 'tile',
        position: { x: 0, y: 0 },
        data: {
          lineOne: 'External destination',
          lineTwo: '',
          variant: TileVariant.Default,
          sectionLabelId: AfterDeath.None,
          isNewTile: getIsNewNodeById(outgoingTransferNodeId),
          height: undefined,
        },
      },
      categorizationType: GraphNodeCategorizationType.ExternalSource,
    });
  }

  const incomingTransferSourceNodes = compact(
    getNodes(incomingExternalTransfers).map((transfer) => {
      const transferAmount = transfer.transferValue || new Decimal(0);
      const targetId =
        transfer.destinationEntity?.id ||
        transfer.destinationIndividual?.id ||
        transfer.destinationOrganization?.id;

      if (!targetId) {
        return null;
      }

      if (
        !visibleNodeIds.includes(
          getNodeId({ id: targetId, afterDeath: AfterDeath.None })
        )
      ) {
        return null;
      }

      return {
        transferAmount,
        targetId,
      };
    })
  );
  const incomingTransferNodeId = getExternalTransferNodeId('incoming');

  if (incomingTransferSourceNodes.length) {
    graph.addNodeSafe(incomingTransferNodeId, {
      data: {
        afterDeath: AfterDeath.None,
        id: EXTERNAL_TRANSFER_NODE_IDENTIFIER,
      },
      node: {
        id: incomingTransferNodeId,
        type: 'tile',
        position: { x: 0, y: 0 },
        data: {
          lineOne: 'External source',
          lineTwo: '',
          variant: TileVariant.Primary,
          sectionLabelId: AfterDeath.None,
          isNewTile: getIsNewNodeById(incomingTransferNodeId),
          height: undefined,
        },
      },
      categorizationType: GraphNodeCategorizationType.ExternalSource,
    });
  }

  const pouroverEdges = viz.edges.filter(getIsPouroverEdge);

  const idsWithIncomingPouroverDispositions = uniq(
    pouroverEdges.map(({ to }) =>
      getNodeId({ id: to.node.id, afterDeath: to.afterDeath })
    )
  );
  const idsWithOutgoingPouroverDispositions = uniq(
    pouroverEdges.map(({ from }) =>
      getNodeId({ id: from.node.id, afterDeath: from.afterDeath })
    )
  );

  // get the list of IDs that have a transfer attached -- we don't care about order, source/destination, etc.,
  // just that the ID is involved in a transfer
  const idsWithTransfers: string[] = uniq(
    getNodes(hypotheticalTransfers).reduce<string[]>((acc, t) => {
      return acc.concat(
        compact([
          t.sourceEntity?.id,
          t.sourceIndividual?.id,
          t.destinationEntity?.id,
          t.destinationIndividual?.id,
          t.destinationOrganization?.id,
        ])
      );
    }, [])
  );

  // Update the helper function with proper type
  function getTaxBeforeAndAfterData(
    taxSummary:
      | NonNullable<EstateWaterfall_TaxSummaryV2Fragment>
      | NonNullable<EstateWaterfall_GiftTaxSummaryFragment>,
    afterDeath: AfterDeath
  ) {
    // Get the current tax value
    const currentTaxValue = getCombinedTaxStateAndFederalAndGst(taxSummary);

    if (!baseNodes) {
      return {
        shouldShowBeforeAndAfter: false,
        beforeValue: null,
        afterValue: currentTaxValue,
      };
    }

    // Find the corresponding tax summary in the base waterfall
    const baseViz = waterfall.nonHypotheticalVisualizationWithProjections;
    let baseTaxSummary;

    if (afterDeath === AfterDeath.First) {
      baseTaxSummary = baseViz.firstDeathTaxSummaryV2;
    } else if (afterDeath === AfterDeath.Second) {
      baseTaxSummary = baseViz.secondDeathTaxSummaryV2;
    } else {
      // Gift tax case (AfterDeath.None)
      baseTaxSummary = baseViz.beforeFirstDeathTaxSummaryV2;
    }

    // Get the base tax value
    const baseTaxValue = getCombinedTaxStateAndFederalAndGst(baseTaxSummary);

    // Only show before/after if there's a difference and both values exist
    const shouldShowBeforeAndAfter =
      !!baseTaxValue && !!currentTaxValue && !baseTaxValue.eq(currentTaxValue);

    return {
      shouldShowBeforeAndAfter,
      beforeValue: baseTaxValue,
      afterValue: currentTaxValue,
    };
  }

  // Build the estate tax nodes inclusive of state estate taxes
  const estateTaxSummaries: (
    | EstateWaterfall_TaxSummaryV2Fragment
    | undefined
    | null
  )[] = [firstDeathTaxSummaryV2, secondDeathTaxSummaryV2];

  estateTaxSummaries.forEach((summary, idx) => {
    if (visualizationConfig?.hideTaxTiles) {
      return;
    }
    const afterDeath = idx === 0 ? AfterDeath.First : AfterDeath.Second;
    const priorDeathEvent = idx === 0 ? AfterDeath.None : AfterDeath.First;
    // if there are any nodes with a tax transfer into this tax node, we need to show the tax node
    // or the diagram will not render
    const deathEventHasPriorDeathEventNodesWithTaxTransfers = vizNodes.some(
      (node) =>
        hasDisplayableTax(node.transferredToEstateTax) &&
        node.afterDeath === priorDeathEvent
    );
    const id = getTaxNodeId({ afterDeath });
    const estateTaxSumForDeath = getCombinedTaxStateAndFederalAndGst(summary);

    if (
      (summary && hasDisplayableTax(estateTaxSumForDeath)) ||
      deathEventHasPriorDeathEventNodesWithTaxTransfers
    ) {
      let lineTwo: ReactNode | undefined = undefined;

      if (summary) {
        // Check for before/after data
        const {
          shouldShowBeforeAndAfter: estateTaxShowBeforeAfter,
          beforeValue: estateTaxBeforeValue,
          afterValue: estateTaxAfterValue,
        } = getTaxBeforeAndAfterData(summary, afterDeath);

        if (estateTaxShowBeforeAfter) {
          lineTwo = (
            <TileNodeBeforeAndAfter
              before={formatCurrency(estateTaxBeforeValue!, {
                notation: 'compact',
              })}
              after={formatCurrency(estateTaxAfterValue, {
                notation: 'compact',
              })}
            />
          );
        } else {
          lineTwo = formatCurrency(estateTaxSumForDeath, {
            notation: 'compact',
          });
        }
      }

      if (visualizationConfig?.hideEntityValues) {
        lineTwo = undefined;
      }

      graph.addNodeSafe(id, {
        data: { afterDeath, id: ESTATE_TAX_NODE_IDENTIFIER },
        node: {
          id,
          hidden: visualizationConfig?.hideTaxTiles,
          type: 'tile',
          position: { x: 0, y: 0 },
          data: {
            lineOne: (
              <TaxLineOne
                summary={summary!}
                afterDeath={afterDeath}
                firstGrantorDeathId={firstGrantorDeath.id}
              />
            ),
            lineTwo,
            lineThree: <TaxLineThree summary={summary!} />,
            variant: TileVariant.Destructive,
            sectionLabelId: afterDeath,
            isNewTile: getIsNewNodeById(id),
            showEstateWaterfallTaxBreakoutInEditModal: true,
            children: (
              <TaxBreakout
                summary={summary!}
                variant={TileVariant.Destructive}
                nodeId={id}
                afterDeath={afterDeath}
                firstGrantorDeathId={firstGrantorDeath.id}
              />
            ),
            height: undefined,
            editModal: ({ isOpen, onClose }) => (
              <EstateWaterfallTileEditModal isOpen={isOpen} onClose={onClose} />
            ),
          },
        },
        categorizationType: GraphNodeCategorizationType.EstateTax,
      });
    }
  });

  // Build the gift tax nodes for hypothetical waterfalls
  const giftTaxSummary = beforeFirstDeathTaxSummaryV2;
  if (isHypotheticalWaterfall(waterfall)) {
    const afterDeath = AfterDeath.None;
    const giftTaxSumForDeath =
      getCombinedTaxStateAndFederalAndGst(giftTaxSummary);
    if (
      giftTaxSumForDeath?.greaterThan(0) &&
      !visualizationConfig?.hideTaxTiles
    ) {
      // Check for before/after data
      const {
        shouldShowBeforeAndAfter: giftTaxShowBeforeAfter,
        beforeValue: giftTaxBeforeValue,
        afterValue: giftTaxAfterValue,
      } = getTaxBeforeAndAfterData(giftTaxSummary!, afterDeath);

      let lineTwo: ReactNode;
      if (giftTaxShowBeforeAfter) {
        lineTwo = (
          <TileNodeBeforeAndAfter
            before={formatCurrency(giftTaxBeforeValue!, {
              notation: 'compact',
            })}
            after={formatCurrency(giftTaxAfterValue, {
              notation: 'compact',
            })}
          />
        );
      } else {
        lineTwo = formatCurrency(giftTaxSumForDeath, {
          notation: 'compact',
        });
      }

      if (visualizationConfig?.hideEntityValues) {
        lineTwo = undefined;
      }

      const id = getGiftTaxNodeId({ afterDeath });
      graph.addNodeSafe(id, {
        data: { afterDeath, id },
        node: {
          id,
          hidden: visualizationConfig?.hideTaxTiles,
          type: 'tile',
          position: { x: 0, y: 0 },
          data: {
            lineOne: 'Gift tax',
            lineTwo,
            variant: TileVariant.Destructive,
            sectionLabelId: afterDeath,
            isNewTile: getIsNewNodeById(id),
            height: undefined,
            editModal: ({ isOpen, onClose }) => (
              <EstateWaterfallTileEditModal isOpen={isOpen} onClose={onClose} />
            ),
          },
        },
        categorizationType: GraphNodeCategorizationType.GiftTax,
      });
    }
  }

  // Add the nodes
  vizNodes.forEach((node) => {
    const nodeId = getNodeId(node);

    const tile = buildTileFromNode({
      visualizationConfig,
      nodeFragment: node,
      isNewTile: getIsNewNodeById(nodeId),
      hasHypotheticalTransfer: idsWithTransfers.includes(node.id),
      hasIncomingPouroverDisposition:
        idsWithIncomingPouroverDispositions.includes(nodeId),
      hasOutgoingPouroverDisposition:
        idsWithOutgoingPouroverDispositions.includes(nodeId),
      baseNodes,
      isHypotheticalWaterfall: isHypothetical,
    });

    const categorizationType = getCategorizationType(node);
    if (!tile) return;
    if (!categorizationType) return;

    graph.addNodeSafe(nodeId, {
      data: node,
      node: {
        ...tile,
        data: {
          ...tile.data,
          debug: {
            vizNodeIds: [node.id],
            afterDeath: node.afterDeath,
            isGroupNode: false,
          },
        },
      },
      categorizationType,
    });

    // Add the outgoing external transfer edge from the node that originated the transfer.
    // If the node is part of a visualization group, the aggregated group-level edge is
    // created below in the grouped-nodes loop; adding the per-entity edge here would
    // result in duplicate transfer lines to the same group.
    const outgoingTransfer = outgoingTransferSourceNodes.find(
      ({ sourceId }) => sourceId === node.id
    );
    if (
      outgoingTransfer &&
      node.afterDeath === AfterDeath.None &&
      !node.group?.id
    ) {
      const source = getNodeId(node);
      const target = outgoingTransferNodeId;
      const edge = createEdge({
        source,
        target,
        // Match the horizontal routing that drawEdges uses for Transfer-kind edges
        // so external-destination transfers render left-to-right rather than as
        // vertical bottom-to-top lines.
        sourceHandle: Position.Right,
        targetHandle: Position.Left,
        data: {
          variant: 'secondary',
          edgeLabel: getFeatureFlaggedEdgeLabel({
            source,
            target,
            variant: 'secondary',
            label: 'Transfer',
            value: formatCurrency(outgoingTransfer.transferAmount, {
              notation: 'compact',
            }),
          }),
        },
      });
      graph.addEdgeSafe(source, target, {
        type: 'default',
        edge,
      });
    }

    // Add the incoming transfer edge from the node that originated the transfer.
    // Skip grouped nodes for the same reason as the outgoing case above: the group-level
    // edge is created separately below and would otherwise duplicate this one.
    const incomingTransfer = incomingTransferSourceNodes.find(
      (transfer) => transfer.targetId === node.id
    );
    if (
      incomingTransfer &&
      node.afterDeath === AfterDeath.None &&
      !node.group?.id
    ) {
      const target = getNodeId(node);
      const source = incomingTransferNodeId;
      const edge = createEdge({
        source,
        target,
        // Match the horizontal routing that drawEdges uses for Transfer-kind edges
        // so external-source transfers render left-to-right rather than as
        // vertical bottom-to-top lines.
        sourceHandle: Position.Right,
        targetHandle: Position.Left,
        data: {
          variant: 'secondary',
          edgeLabel: getFeatureFlaggedEdgeLabel({
            source,
            target,
            variant: 'secondary',
            label: 'Transfer',
            value: formatCurrency(incomingTransfer.transferAmount, {
              notation: 'compact',
            }),
          }),
        },
      });
      graph.addEdgeSafe(source, target, {
        type: 'default',
        edge,
      });
    }

    // Add the gift edge from the node that originated the transfer
    if (
      node.transferredToGiftingTax?.toNumber() &&
      !visualizationConfig?.hideTaxTiles
    ) {
      const afterDeath = node.afterDeath;
      const giftTaxNodeId = getGiftTaxNodeId({ afterDeath });

      const source = getNodeId(node);

      addTaxEdge({
        graph,
        source,
        target: giftTaxNodeId,
        value: node.transferredToGiftingTax,
      });
    }

    // Add the edge to the estate tax node. We do this in the node iteration vs. the edge iteration,
    // because some edges are hidden when the value of a tile is depleted to 0, see: https://linear.app/luminary/issue/T1-279
    //
    // Only include the node if there is an actual value, 0 is invalid
    // https://withluminary.slack.com/archives/C05BPQS8CG4/p1692748772395659?thread_ts=1692748503.489729&cid=C05BPQS8CG4
    // TODO (LUM-2026): Remove this when Edu adds it to the backend
    if (
      // if the amount paid to taxes is less than zero, ignore it as invalid
      hasDisplayableTax(node.transferredToEstateTax) &&
      !visualizationConfig?.hideTaxTiles
    ) {
      const source = nodeId;

      const taxTarget = getTaxNodeId({
        afterDeath: getEstateTaxAfterDeath(node.afterDeath),
      });

      addTaxEdge({
        graph,
        source,
        target: taxTarget,
        value: node.transferredToEstateTax,
      });
    }
  });

  // Add the grouped nodes + tax transfer edge
  Object.entries(groupedNodesByGroupNodeId).forEach(
    ([groupNodeId, groupNodes]) => {
      // If the GraphVizGroup for this set of grouped nodes isn't present, log an
      // error and skip rendering this group instead of throwing.
      const group = visualizationGroupsByGroupNodeId[groupNodeId];

      if (!group) {
        diagnostics.warn(
          `Orphan group reference: missing GraphVizGroup for ${groupNodeId}`
        );
        return;
      }

      const nodeId = groupNodeId;
      const afterDeath = group.namespace as AfterDeath;
      const categorizationType: GraphNodeCategorizationType =
        GraphNodeCategorizationType.GroupNode;

      const shouldTriggerCreateGroupModal =
        group.displayName === SHOW_CREATE_GROUP_MODAL_SENTINEL;

      const lineOne = (() => {
        if (shouldTriggerCreateGroupModal) return '';
        return group.displayName ?? '';
      })();

      const groupedTiles = compact(
        groupNodes.map((node) => {
          const tile = buildTileFromNode({
            visualizationConfig,
            nodeFragment: node,
            isHypotheticalWaterfall: isHypothetical,
          });

          const nodeId = getNodeId(node);

          const correspondingBaseNode =
            baseNodes?.find(
              (n) =>
                getNodeId({ id: n.id, afterDeath: n.afterDeath }) === nodeId
            ) ?? null;

          const {
            beforeActionsValue,
            afterActionsValue,
            shouldShowBeforeAndAfter,
          } = getBeforeAndAfterData(
            node,
            {
              hasIncomingPouroverDisposition:
                idsWithIncomingPouroverDispositions.includes(nodeId),
              hasOutgoingPouroverDisposition:
                idsWithOutgoingPouroverDispositions.includes(nodeId),
            },
            correspondingBaseNode,
            isHypothetical
          );

          if (!tile) return null;

          const { id, data } = tile;

          return {
            tileId: id,
            nodeId: node.id,
            beforeActionsValue,
            afterActionsValue,
            shouldShowBeforeAndAfter,
            ...data,
            value: node.value,
          };
        })
      );

      let beforeActionsTotalValue: Decimal | null = null;
      let afterActionsTotalValue: Decimal | null = null;

      const shouldShowBeforeAndAfter = groupedTiles.some(
        ({ beforeActionsValue, shouldShowBeforeAndAfter }) =>
          !!beforeActionsValue && shouldShowBeforeAndAfter
      );

      if (shouldShowBeforeAndAfter) {
        beforeActionsTotalValue = sumDecimalJS(
          compact(
            groupedTiles.map(
              ({ beforeActionsValue, shouldShowBeforeAndAfter, value }) =>
                shouldShowBeforeAndAfter ? beforeActionsValue : value
            )
          )
        );
        afterActionsTotalValue =
          sumDecimalJS(
            compact(
              groupedTiles.map(({ afterActionsValue }) => afterActionsValue)
            )
          ) ?? new Decimal(0);
      }

      const shouldShowBeforeAndAfterTile =
        beforeActionsTotalValue && afterActionsTotalValue;

      const variant = getGroupNodeTileVariant(groupNodes);

      const lineTwo = (() => {
        if (visualizationConfig?.hideEntityValues) return undefined;

        if (shouldShowBeforeAndAfterTile) {
          // beforeActionsTotalValue and afterActionsTotalValue are guaranteed to be non-null here
          return (
            <TileNodeBeforeAndAfter
              before={formatCurrency(beforeActionsTotalValue!, {
                notation: 'compact',
              })}
              after={formatCurrency(afterActionsTotalValue!, {
                notation: 'compact',
              })}
            />
          );
        }

        return formatCurrency(
          sumDecimalJS(groupNodes.map((node) => node.value)),
          {
            notation: 'compact',
            currencySign: 'accounting',
          }
        );
      })();

      const nodeConfig = waterfall.nodeConfigurations.find(
        (config) => config.nodeID === nodeId
      );

      const node: TileNode = {
        id: nodeId,
        type: 'tile',
        position: { x: 0, y: 0 },
        data: {
          lineOne,
          lineTwo,
          lineThree: undefined,
          variant,
          sectionLabelId: afterDeath,
          isGroupNode: true,
          componentType:
            nodeConfig?.type === GraphVizNodeComponentType.TileIcon
              ? GraphVizNodeComponentType.TileIcon
              : GraphVizNodeComponentType.Tile,
          estateWaterfallConfig: nodeConfig?.estateWaterfallConfig,
          notes: group.description ?? undefined,
          debug: {
            vizNodeIds: groupNodes.map((n) => n.id),
            afterDeath,
            isGroupNode: true,
          },
          slots: {
            Icon: getOutOfEstateCategoryIcon(variant) ?? <Grid01Icon />,
          },
          editModal: ({ isOpen, onClose }) => {
            return (
              <ManageGroupModal
                isOpen={isOpen}
                groupName={group.displayName}
                groupDescription={group.description}
                onClose={onClose}
                namespace={group.namespace as AfterDeath}
                groupedTiles={groupedTiles}
                groupId={group.id}
              />
            );
          },
          children: (
            <GroupNodeItems
              groupNodeId={nodeId}
              nodes={groupNodes}
              shouldTriggerCreateGroupModal={shouldTriggerCreateGroupModal}
              namespace={afterDeath}
              variant={variant}
            />
          ),
          height: undefined,
        },
      };

      // Member records for the presentation "Group details" drawer. Only real
      // `Entity` records have a detail page to open; everything else lists but
      // isn't openable. Deep-link off the nested record's `id` (`node.id`) —
      // the entity id `HOUSEHOLD_ENTITY_DETAILS` expects, matching
      // `resolveNodeTarget`/"Open in app" — not the viz node's top-level `id`.
      const members: GroupNodeMember[] = groupNodes.map((groupNode) => ({
        entityId: groupNode.node.id,
        displayName: isEntityNode(groupNode.node)
          ? getWaterfallNodeDisplayName(groupNode.node)
          : '',
        isOpenableEntity: groupNode.node.__typename === 'Entity',
      }));

      // Add the group node
      graph.addNodeSafe(nodeId, {
        data: { afterDeath, id: group.id, members },
        node,
        categorizationType,
      });

      const groupNodeIds = groupNodes.map(({ id }) => id);
      const incomingTransferAmountSum = sumDecimalJS(
        incomingTransferSourceNodes
          .filter(({ targetId }) => groupNodeIds.includes(targetId))
          .map(({ transferAmount }) => transferAmount)
      );
      const outgoingTransferAmountSum = sumDecimalJS(
        outgoingTransferSourceNodes
          .filter(({ sourceId }) => groupNodeIds.includes(sourceId))
          .map(({ transferAmount }) => transferAmount)
      );

      // The group node is replicated across death sections, but external
      // transfer tiles live outside any section and the per-node loop above
      // only draws these edges for `AfterDeath.None`. Mirror that here so we
      // don't duplicate the transfer line into every death section's copy of
      // the group.
      const isBeforeFirstDeathSection = afterDeath === AfterDeath.None;

      if (
        isBeforeFirstDeathSection &&
        incomingTransferAmountSum.greaterThan(0)
      ) {
        const source = incomingTransferNodeId;
        const target = nodeId;
        const incomingEdge = createEdge({
          source,
          target,
          // External transfers are horizontal; align grouped aggregates with the
          // per-node external transfer edges above.
          sourceHandle: Position.Right,
          targetHandle: Position.Left,
          data: {
            variant: 'secondary',
            edgeLabel: getFeatureFlaggedEdgeLabel({
              source,
              target,
              variant: 'secondary',
              label: 'Transfer',
              value: formatCurrency(incomingTransferAmountSum, {
                notation: 'compact',
              }),
            }),
          },
        });
        graph.addEdgeSafe(source, target, {
          type: 'default',
          edge: incomingEdge,
        });
      }

      if (
        isBeforeFirstDeathSection &&
        outgoingTransferAmountSum.greaterThan(0)
      ) {
        const source = nodeId;
        const target = outgoingTransferNodeId;
        const outgoingEdge = createEdge({
          source,
          target,
          // External transfers are horizontal; align grouped aggregates with the
          // per-node external transfer edges above.
          sourceHandle: Position.Right,
          targetHandle: Position.Left,
          data: {
            variant: 'secondary',
            edgeLabel: getFeatureFlaggedEdgeLabel({
              source,
              target,
              variant: 'secondary',
              label: 'Transfer',
              value: formatCurrency(outgoingTransferAmountSum, {
                notation: 'compact',
              }),
            }),
          },
        });
        graph.addEdgeSafe(source, target, {
          type: 'default',
          edge: outgoingEdge,
        });
      }

      const transferTaxSum = sumDecimalJS(
        groupNodes.map((n) => n.transferredToEstateTax ?? new Decimal(0))
      );

      const giftTaxSum = sumDecimalJS(
        groupNodes.map((n) => n.transferredToGiftingTax ?? new Decimal(0))
      );

      // Add the transfer tax sum from nodes within the group to the tax tile
      if (transferTaxSum.toNumber() && !visualizationConfig?.hideTaxTiles) {
        const source = nodeId;

        const taxTarget = getTaxNodeId({
          afterDeath: getEstateTaxAfterDeath(afterDeath),
        });

        addTaxEdge({
          graph,
          source,
          target: taxTarget,
          value: transferTaxSum,
        });
      }

      // Add edge from group to associated gift tax node
      if (giftTaxSum.toNumber() && !visualizationConfig?.hideTaxTiles) {
        const source = nodeId;

        const taxTarget = getGiftTaxNodeId({
          afterDeath,
        });

        addTaxEdge({
          graph,
          source,
          target: taxTarget,
          value: giftTaxSum,
        });
      }
    }
  );

  // Ensure every section has at least one visible tile so the section layout works
  deathSections.forEach((afterDeath, idx) => {
    let sectionHasTile = false;

    graph.forEachNode((_nodeId, attrs) => {
      const isGraphNodeHidden =
        attrs.node.type === 'tile' &&
        'hidden' in attrs.node &&
        attrs.node.hidden;

      if (
        attrs.node.type === 'tile' &&
        attrs.data.afterDeath === afterDeath &&
        !isGraphNodeHidden
      ) {
        sectionHasTile = true;
      }
    });

    if (!sectionHasTile) {
      const placeholderId = `${SECTION_PLACEHOLDER_TILE_NODE_IDENTIFIER}-${afterDeath}-${idx}`;

      graph.addNodeSafe(placeholderId, {
        data: { afterDeath, id: placeholderId },
        node: {
          id: placeholderId,
          type: 'tile',
          position: { x: 0, y: 0 },
          data: {
            lineOne: '',
            label: '',
            variant: TileVariant.Pixel,
            sectionLabelId: afterDeath,
          },
        },
        categorizationType: GraphNodeCategorizationType.Individual,
      });
    }
  });

  // Draw the edges between the nodes
  drawEdges({
    graph,
    viz,
  });

  graph.setAttribute('firstPrimaryClientDeathId', firstGrantorDeath.id);
  graph.setAttribute('waterfall', waterfall);

  return graph;
}
